"use client";

import { useCallback, useEffect, useState } from "react";
import {
  ArrowLeft,
  Check,
  Copy,
  Plug,
  RotateCw,
  ShieldAlert,
  Terminal,
} from "lucide-react";

import { Button } from "@/shadcn-components/ui/button";
import { Card, CardContent } from "@/shadcn-components/ui/card";
import { Input } from "@/shadcn-components/ui/input";
import { Label } from "@/shadcn-components/ui/label";
import { Separator } from "@/shadcn-components/ui/separator";
import { Switch } from "@/shadcn-components/ui/switch";
import { useSettingsStore } from "@/lib/settings";
import { usePlatform } from "@/platform/PlatformProvider";
import type { McpBridgeStatus, McpCallLogEntry } from "@/platform/types";

interface McpServerContentProps {
  onBack: () => void;
}

const IDLE_STATUS: McpBridgeStatus = {
  running: false,
  port: null,
  token: null,
  toolCount: 0,
  callCount: 0,
};

function connectCommand(status: McpBridgeStatus): string {
  const port = status.port ?? 7717;
  const auth = status.token ? ` --header "Authorization: Bearer ${status.token}"` : "";
  return `claude mcp add --transport http vybe-trader http://127.0.0.1:${port}/mcp${auth}`;
}

/** "just now" / "6 d ago" for the token's issue date. 0 = unknown. */
function issuedLabel(epochMillis: number): string {
  if (!epochMillis) return "issued on first start";
  const seconds = Math.max(0, Math.round((Date.now() - epochMillis) / 1000));
  if (seconds < 60) return "issued just now";
  const units: Array<[number, string]> = [
    [60, "min"],
    [60, "h"],
    [24, "d"],
  ];
  let value = seconds;
  let unit = "s";
  for (const [step, name] of units) {
    if (value < step) break;
    value = Math.floor(value / step);
    unit = name;
  }
  return `issued ${value} ${unit} ago`;
}

/**
 * MCP Server sub-sheet — a peer of Interface / Charts / Settings.
 *
 * Rendered only when `platform.mcp` exists (desktop). The bridge itself lives
 * in the Tauri shell; this screen is its control surface and activity view.
 */
export function McpServerContent({ onBack }: McpServerContentProps) {
  const platform = usePlatform();
  const bridge = platform.mcp;
  const { settings, updateSetting } = useSettingsStore();

  const [status, setStatus] = useState<McpBridgeStatus>(IDLE_STATUS);
  const [calls, setCalls] = useState<McpCallLogEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [portDraft, setPortDraft] = useState(String(settings.mcpServerPort));
  const [failure, setFailure] = useState<string | null>(null);
  const [tokenIssued, setTokenIssued] = useState(0);
  const [configPath, setConfigPath] = useState("");
  const [pathCopied, setPathCopied] = useState(false);

  // Subscribe to the bridge and take its current state on mount.
  useEffect(() => {
    if (!bridge) return;
    setCalls(bridge.recentCalls());
    const offStatus = bridge.onStatusChange(setStatus);
    const offCall = bridge.onCall((entry) =>
      setCalls((prev) => [entry, ...prev].slice(0, 50)),
    );
    void bridge.getStatus().then(setStatus).catch(() => undefined);
    return () => {
      offStatus();
      offCall();
    };
  }, [bridge]);

  // Mirror the shell-owned config into the settings store. The file is the
  // authority — it survives a localStorage clear, so on any disagreement it
  // wins and the store is corrected, never the other way round.
  useEffect(() => {
    if (!bridge) return;
    let cancelled = false;
    void bridge
      .loadConfig()
      .then((cfg) => {
        if (cancelled) return;
        setTokenIssued(cfg.tokenIssued);
        if (cfg.port !== settings.mcpServerPort) {
          updateSetting("mcpServerPort", cfg.port);
          setPortDraft(String(cfg.port));
        }
        if (cfg.autostart !== settings.mcpAutostart) {
          updateSetting("mcpAutostart", cfg.autostart);
        }
        if (cfg.allowTradingWrites !== settings.mcpAllowTradingWrites) {
          updateSetting("mcpAllowTradingWrites", cfg.allowTradingWrites);
        }
        bridge.setAllowTradingWrites(cfg.allowTradingWrites);
      })
      .catch(() => undefined);
    void bridge.configPath().then((p) => {
      if (!cancelled) setConfigPath(p);
    }).catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // Runs once per bridge: this reconciles the stored state, and must not
    // re-fire when it writes the very settings it reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bridge]);

  /**
   * Write the four non-secret fields through to `mcp.json`. Called after every
   * change so the file, not localStorage, is what the next launch reads.
   */
  const persist = useCallback(
    (patch: Partial<{
      enabled: boolean;
      autostart: boolean;
      port: number;
      allowTradingWrites: boolean;
    }>) => {
      if (!bridge) return;
      void bridge
        .saveConfig({
          enabled: patch.enabled ?? settings.mcpServerEnabled,
          autostart: patch.autostart ?? settings.mcpAutostart,
          port: patch.port ?? settings.mcpServerPort,
          allowTradingWrites:
            patch.allowTradingWrites ?? settings.mcpAllowTradingWrites,
        })
        .catch((e) =>
          setFailure(
            `Settings could not be saved to disk: ${
              e instanceof Error ? e.message : String(e)
            }`,
          ),
        );
    },
    [
      bridge,
      settings.mcpServerEnabled,
      settings.mcpAutostart,
      settings.mcpServerPort,
      settings.mcpAllowTradingWrites,
    ],
  );

  const toggleEnabled = useCallback(
    async (next: boolean) => {
      if (!bridge) return;
      setBusy(true);
      setFailure(null);
      try {
        if (next) {
          const started = await bridge.start({
            port: settings.mcpServerPort,
            allowTradingWrites: settings.mcpAllowTradingWrites,
          });
          setStatus(started);
        } else {
          await bridge.stop();
        }
        updateSetting("mcpServerEnabled", next);
        persist({ enabled: next });
        // A first start mints and stores the token — pick up its date.
        void bridge.loadConfig().then((cfg) => setTokenIssued(cfg.tokenIssued));
      } catch (e) {
        // Leave the switch off: the listener did not come up.
        setFailure(e instanceof Error ? e.message : String(e));
        updateSetting("mcpServerEnabled", false);
        persist({ enabled: false });
      } finally {
        setBusy(false);
      }
    },
    [
      bridge,
      settings.mcpServerPort,
      settings.mcpAllowTradingWrites,
      updateSetting,
      persist,
    ],
  );

  const toggleAutostart = useCallback(
    (next: boolean) => {
      updateSetting("mcpAutostart", next);
      persist({ autostart: next });
    },
    [updateSetting, persist],
  );

  const commitPort = useCallback(() => {
    const parsed = Number.parseInt(portDraft, 10);
    if (!Number.isFinite(parsed) || parsed < 1024 || parsed > 65535) {
      setPortDraft(String(settings.mcpServerPort));
      return;
    }
    if (parsed !== settings.mcpServerPort) {
      updateSetting("mcpServerPort", parsed);
      persist({ port: parsed });
    }
  }, [portDraft, settings.mcpServerPort, updateSetting, persist]);

  const toggleWrites = useCallback(
    (next: boolean) => {
      updateSetting("mcpAllowTradingWrites", next);
      bridge?.setAllowTradingWrites(next);
      persist({ allowTradingWrites: next });
    },
    [bridge, updateSetting, persist],
  );

  const rotateToken = useCallback(async () => {
    if (!bridge) return;
    setBusy(true);
    setFailure(null);
    try {
      const next = await bridge.rotateToken();
      setStatus(next);
      const cfg = await bridge.loadConfig();
      setTokenIssued(cfg.tokenIssued);
    } catch (e) {
      setFailure(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [bridge]);

  const copyPath = useCallback(() => {
    if (!configPath) return;
    void navigator.clipboard.writeText(configPath).then(() => {
      setPathCopied(true);
      setTimeout(() => setPathCopied(false), 1600);
    });
  }, [configPath]);

  const copyCommand = useCallback(() => {
    void navigator.clipboard.writeText(connectCommand(status)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  }, [status]);

  if (!bridge) return null;

  const running = status.running;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-6 border-b border-border">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack} className="h-8 w-8">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
              <Plug className="h-5 w-5 text-blue-600" />
            </div>
            <div className="space-y-1">
              <h2 className="text-lg font-semibold">MCP Server</h2>
              <p className="text-sm text-muted-foreground">
                Expose the Vybe DSL to local agent tools
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
        {/* Connection */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Plug className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-muted-foreground text-sm uppercase font-medium tracking-wide">
              CONNECTION
            </h3>
            <span
              className={`ml-auto font-mono text-[10.5px] font-medium ${
                running ? "text-green-500" : "text-muted-foreground"
              }`}
            >
              ● {running ? `listening · 127.0.0.1:${status.port}` : "stopped"}
            </span>
          </div>
          <Card>
            <CardContent className="p-0">
              <div className="flex items-center justify-between p-3">
                <div className="flex-1 min-w-0 mr-3">
                  <Label htmlFor="mcp-enabled" className="text-base font-medium cursor-pointer">
                    Expose MCP server
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Loopback only — never reachable from the network
                  </p>
                </div>
                <Switch
                  id="mcp-enabled"
                  disabled={busy}
                  checked={running}
                  onCheckedChange={(v) => void toggleEnabled(v)}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between p-3">
                <div className="flex-1 min-w-0 mr-3">
                  <Label htmlFor="mcp-autostart" className="text-base font-medium cursor-pointer">
                    Start with the app
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Bring the server back up automatically after a restart
                  </p>
                </div>
                <Switch
                  id="mcp-autostart"
                  checked={settings.mcpAutostart}
                  onCheckedChange={toggleAutostart}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between p-3">
                <div className="flex-1 min-w-0 mr-3">
                  <Label htmlFor="mcp-port" className="text-base font-medium">
                    Port
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {running ? "Restart the server to change the port" : "127.0.0.1"}
                  </p>
                </div>
                <Input
                  id="mcp-port"
                  className="w-24 font-mono text-sm"
                  value={portDraft}
                  disabled={running}
                  inputMode="numeric"
                  onChange={(e) => setPortDraft(e.target.value)}
                  onBlur={commitPort}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between p-3">
                <div className="flex-1 min-w-0 mr-3">
                  <Label className="text-base font-medium flex items-center gap-2">
                    Bearer token
                    <span className="text-[10px] font-mono font-medium uppercase tracking-wide text-green-500 border border-green-500/40 rounded-full px-2 py-px">
                      persisted
                    </span>
                  </Label>
                  <p className="text-sm text-muted-foreground font-mono truncate">
                    {status.token ?? "minted when the server starts"}
                  </p>
                  <p className="text-xs text-muted-foreground font-mono">
                    {issuedLabel(tokenIssued)}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy || !status.token}
                  onClick={() => void rotateToken()}
                >
                  <RotateCw className="h-3.5 w-3.5 mr-1.5" />
                  Rotate
                </Button>
              </div>
            </CardContent>
          </Card>
          {failure && (
            <p className="text-sm text-red-500 mt-2 flex items-start gap-2">
              <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{failure}</span>
            </p>
          )}
        </div>

        {/* Connect */}
        {running && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Terminal className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-muted-foreground text-sm uppercase font-medium tracking-wide">
                CONNECT CLAUDE CODE
              </h3>
            </div>
            <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 p-3">
              <code className="flex-1 font-mono text-[11px] leading-relaxed break-all text-foreground">
                {connectCommand(status)}
              </code>
              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={copyCommand}>
                {copied ? (
                  <Check className="h-3.5 w-3.5 text-green-500" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Run this <span className="text-green-500 font-medium">once</span> — the token is
              stored on this machine and survives restarts. Only <b>Rotate</b> invalidates it.
            </p>
          </div>
        )}

        {/* Access */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <ShieldAlert className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-muted-foreground text-sm uppercase font-medium tracking-wide">
              ACCESS
            </h3>
          </div>
          <Card>
            <CardContent className="p-0">
              <div className="flex items-center justify-between p-3">
                <div className="flex-1 min-w-0 mr-3">
                  <Label htmlFor="mcp-writes" className="text-base font-medium cursor-pointer">
                    Allow trading writes
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Let connected tools call Orders.Buy / Sell / Market
                  </p>
                </div>
                <Switch
                  id="mcp-writes"
                  checked={settings.mcpAllowTradingWrites}
                  onCheckedChange={toggleWrites}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between p-3">
                <div className="flex-1 min-w-0 mr-3">
                  <Label className="text-base font-medium">Published tools</Label>
                  <p className="text-sm text-muted-foreground">
                    Generated from the DSL registry
                  </p>
                </div>
                <span className="font-mono text-sm text-foreground">
                  {status.toolCount || "—"}
                </span>
              </div>

              <Separator />

              <div className="flex items-center justify-between p-3">
                <div className="flex-1 min-w-0 mr-3">
                  <Label className="text-base font-medium">Config file</Label>
                  <p
                    className="text-sm text-muted-foreground font-mono truncate"
                    title={configPath}
                  >
                    {configPath || "unavailable"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Owner-only. Kept outside browser storage, so clearing local data
                    leaves it intact.
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  disabled={!configPath}
                  onClick={copyPath}
                >
                  {pathCopied ? (
                    <Check className="h-3.5 w-3.5 text-green-500" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Activity */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Terminal className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-muted-foreground text-sm uppercase font-medium tracking-wide">
              ACTIVITY
            </h3>
            <span className="ml-auto font-mono text-[10.5px] text-muted-foreground">
              {status.callCount} calls
            </span>
          </div>
          <Card>
            <CardContent className="p-0">
              {calls.length === 0 ? (
                <p className="p-3 text-sm text-muted-foreground">
                  No calls yet. Connected tools appear here as they run.
                </p>
              ) : (
                calls.slice(0, 12).map((entry, i) => (
                  <div key={entry.id}>
                    {i > 0 && <Separator />}
                    <div className="flex items-center gap-2 px-3 py-2 font-mono text-[11px]">
                      <span
                        className={`shrink-0 ${entry.ok ? "text-green-500" : "text-red-500"}`}
                      >
                        {entry.ok ? "▷" : "✕"}
                      </span>
                      <span className="flex-1 truncate text-foreground" title={entry.call}>
                        {entry.call}
                      </span>
                      <span className="shrink-0 text-muted-foreground">
                        {entry.durationMs} ms
                      </span>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
